import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";

class ApiClient {
  public baseApiUrl: string;
  private axiosInstance: AxiosInstance;

  constructor(
    baseApiUrl: string,
    interceptor?: (
      config: InternalAxiosRequestConfig<any>
    ) => Promise<InternalAxiosRequestConfig<any>>,
    errorHandlers?: {
      onForbidden: () => void;
    }
  ) {
    this.baseApiUrl = baseApiUrl;
    this.axiosInstance = axios.create({ baseURL: baseApiUrl });

    this.axiosInstance.interceptors.request.use(async (config) => {
      if (interceptor) {
        config = await interceptor(config);
      }

      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (res) => res,
      (error: AxiosError) => {
        if (error.response?.status === 403 && !!errorHandlers?.onForbidden) {
          errorHandlers.onForbidden();
          return;
        }

        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, options?: AxiosRequestConfig) {
    //this.controller.abort();
    return await this.axiosInstance
      .get<T>(url, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        //signal: this.controller.signal,
        ...options,
      })
      .then((res) => res.data);
  }

  async post<T>(url: string, data = {}, options?: AxiosRequestConfig) {
    return await this.axiosInstance
      .post<T>(url, data, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        ...options,
      })
      .then((res) => res.data);
  }

  async put<T>(url: string, data = {}, options?: AxiosRequestConfig) {
    return await this.axiosInstance
      .put<T>(url, data, { ...options })
      .then((res) => res.data);
  }

  async patch<T>(url: string, data = {}, options?: AxiosRequestConfig) {
    return await this.axiosInstance
      .patch<T>(url, data, { ...options })
      .then((res) => res.data);
  }

  async delete<T>(url: string, options?: AxiosRequestConfig) {
    return await this.axiosInstance
      .delete<T>(url, { ...options })
      .then((res) => res.data);
  }
  async options<T>(url: string, options?: AxiosRequestConfig) {
    return await this.axiosInstance
      .options<T>(url, { ...options })
      .then((res) => res.data);
  }

  async download<T>(url: string, data = {}, options?: AxiosRequestConfig) {
    return await this.axiosInstance
      .post<T>(url, data, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        responseType: "blob",
        ...options,
      })
      .then((res) => res.data)
      .catch(async (error) => {
        throw {
          response: {
            ...error.response,
            ...{ data: JSON.parse(await error.response.data.text()) },
          },
        };
      });
  }

  async downloadWithFileName<T>(
    url: string,
    data = {},
    options?: AxiosRequestConfig
  ) {
    return await this.axiosInstance
      .post<T>(url, data, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        responseType: "blob",
        ...options,
      })
      .then((res) => {
        // Access the 'content-disposition' header
        const contentDisposition = res.headers["content-disposition"];

        const parseFilename = (contentDisposition: string): string | null => {
          // Looks for filename="<something>" or filename=something
          const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
            contentDisposition
          );
          if (match != null && match[1]) {
            // Remove surrounding quotes if present
            return match[1].replace(/['"]/g, "");
          }
          return null;
        };

        return {
          blobPart: res.data,
          fileName: parseFilename(contentDisposition),
        };
      })
      .catch(async (error) => {
        throw {
          response: {
            ...error.response,
            ...{ data: JSON.parse(await error.response.data.text()) },
          },
        };
      });
  }
}

export default ApiClient;
